import ReactDOMServer from "react-dom/server";
import { FeatureFlag } from "../../featureFlags/FeatureFlagsModels";
import { UserAccessType } from "../../user/Models";
import { ChartChatMessage, ChatMessage } from "../chatModels";
import { renderChartMessagesToDataUrls } from "./chart_renderer/exportUtils";
import { MarkdownRenderer } from "./MarkdownRenderer";
import type { RootState } from "../../app/Store";

export const copyConversationToClipboard = async (
  messages: ChatMessage[],
  isUserEngineerOrAbove: boolean
) => {
  const isChartMessage = (msg: ChatMessage): msg is ChartChatMessage => msg.messageType === "chart";

  const plainText = messages
    .filter((msg) => isUserEngineerOrAbove || msg.messageType !== "code_interpreter")
    .map((msg) => {
      let content: string;
      switch (msg.messageType) {
        case "text":
          content = msg.content;
          if (msg.suggestions) {
            content += ` (Suggestions: ${msg.suggestions.map((s) => s.text).join(", ")})`;
          }
          break;
        case "chart":
          content = `Chart (${msg.content.title})`;
          break;
        case "code_interpreter":
          content = `Sunnie (executes code):\n\`\`\`python\n${msg.content}\n\`\`\``;
          break;
        case "reasoning":
          content = msg.content;
          break;
        default:
          return "";
      }
      return `${msg.origin === "user" ? "You" : "Sunnie"}: ${content}`;
    })
    .join("\n\n");

  navigator.clipboard.writeText(plainText);

  if (!globalThis.ClipboardItem) {
    // browser does not support rich clipboard items
    return;
  }

  const chartMessagesDataUrls = await renderChartMessagesToDataUrls(messages.filter(isChartMessage));

  const htmlText = messages
    .filter((msg) => isUserEngineerOrAbove || msg.messageType !== "code_interpreter")
    .map((msg) => {
      if (msg.messageType === "chart") {
        const chartImage = chartMessagesDataUrls[msg.id];
        return `<div>
          <strong>${msg.origin === "user" ? "You" : "Sunnie"}:</strong>
          <img src="${chartImage}" alt="${msg.content.title}" style="max-width:300px;max-height:200px;"/>
        </div>`;
      }
      if (msg.messageType === "code_interpreter") {
        const markdownString = ReactDOMServer.renderToString(
          <MarkdownRenderer content={`\`\`\`python\n${msg.content}\n\`\`\``} />
        );
        return `<div><strong>Sunnie (executes code):</strong> <span>${markdownString}</span></div>`;
      }
      let content = msg.content;

      if (msg.messageType === "text" && msg.suggestions) {
        const suggestions = msg.suggestions
          .map((suggestion) => `${suggestion.prefixEmoji} ${suggestion.text}`.trim())
          .join(", ");
        content += `\n\n<em>Suggestions: ${suggestions}</em>`;
      }
      const markdownString = ReactDOMServer.renderToString(<MarkdownRenderer content={content} />);
      return `<div><strong>${msg.origin === "user" ? "You" : "Sunnie"}:</strong> <span>${markdownString}</span></div>`;
    })
    .join("<br/><br/>");
  const clipboardItem = new ClipboardItem({
    "text/html": new Blob([htmlText], { type: "text/html" }),
    "text/plain": new Blob([plainText], { type: "text/plain" }),
  });
  navigator.clipboard.write([clipboardItem]).catch(() => {
    navigator.clipboard.writeText(plainText);
  });
};

export const formatMessageTime = (date: Date | number = Date.now()): string => {
  return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const isAiChatEnabledForUser = (
  userSiteIds: string[],
  sitesFeatureFlags: Record<string, FeatureFlag[]>,
  userType: UserAccessType
): boolean => {
  if (userType >= UserAccessType.Engineer) {
    return true;
  }
  if (userType < UserAccessType.Operator) {
    return false;
  }
  return userSiteIds.some((siteId) =>
    sitesFeatureFlags[siteId]?.includes(FeatureFlag.IsAiChatAgentEnabled)
  );
};

export const selectIsMultisiteAiChatEnabled = (state: RootState) =>
  isAiChatEnabledForUser(state.user.siteIds, state.featureFlags.sitesFeatureFlags, state.user.userType);

export const isElementInViewport = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};
